import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Sparkles, MessageSquare, Send, CheckCircle2, RefreshCw } from "lucide-react";

const catColor = {
  behavioral: "text-purple-400 border-purple-400/30 bg-purple-400/10",
  technical: "text-primary border-primary/30 bg-primary/10",
  system_design: "text-accent border-accent/30 bg-accent/10",
};

export default function Interview() {
  const [questions, setQuestions] = useState([]);
  const [role, setRole] = useState("");
  const [active, setActive] = useState(null);
  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [gen, setGen] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/interview/history");
      setHistory(data.interviews);
    } catch (err) {
      console.error("Failed to load interview history", err);
    }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  useEffect(() => {
    const onSwitch = () => { setQuestions([]); setActive(null); setAnswer(""); setFeedback(null); setRole(""); loadHistory(); };
    window.addEventListener("adyap:goal-switched", onSwitch);
    return () => window.removeEventListener("adyap:goal-switched", onSwitch);
  }, [loadHistory]);

  const generate = async () => {
    setGen(true); setFeedback(null); setActive(null);
    try {
      const { data } = await api.post("/interview/generate", {});
      setQuestions(data.questions); setRole(data.role_target);
      if (!data.questions.length) toast.error("Could not generate questions. Try again.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setGen(false); }
  };

  const submit = async () => {
    if (!answer.trim()) return;
    setEvaluating(true);
    try {
      const { data } = await api.post("/interview/mock", { question: active.question, category: active.category, user_answer: answer });
      setFeedback(data);
      loadHistory();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setEvaluating(false); }
  };

  const pick = (q) => { setActive(q); setAnswer(""); setFeedback(null); };

  return (
    <AppLayout>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-xs tracking-wider uppercase font-semibold text-zinc-500">AI Mock Interview</p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-extrabold mt-1">Interview Prep</h1>
        </div>
        <button data-testid="generate-questions-btn" onClick={generate} disabled={gen}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 px-5 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-60">
          {gen ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate questions
        </button>
      </div>

      {!questions.length && !active && (
        <div className="glass rounded-2xl p-12 text-center" data-testid="interview-empty">
          <MessageSquare className="w-10 h-10 text-primary mx-auto mb-3" />
          <p className="text-zinc-400">Generate role-specific questions and practice with instant AI feedback.</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {questions.length > 0 && (
          <div className="space-y-3" data-testid="question-list">
            <p className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Questions for {role}</p>
            {questions.map((q, i) => (
              <button key={i} data-testid={`question-${i}`} onClick={() => pick(q)}
                className={`w-full text-left glass rounded-xl p-4 transition-all hover:border-white/25 ${active?.question === q.question ? "border-primary" : ""}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] tracking-wider uppercase font-semibold border rounded-full px-2 py-0.5 ${catColor[q.category] || catColor.technical}`}>{q.category?.replace("_", " ")}</span>
                  <span className="text-[10px] text-zinc-500 uppercase">{q.difficulty}</span>
                </div>
                <p className="text-sm">{q.question}</p>
              </button>
            ))}
          </div>
        )}

        {active && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6 h-fit" data-testid="answer-panel">
            <span className={`text-[10px] tracking-wider uppercase font-semibold border rounded-full px-2 py-0.5 ${catColor[active.category] || catColor.technical}`}>{active.category?.replace("_", " ")}</span>
            <h2 className="font-heading text-lg font-semibold mt-3">{active.question}</h2>
            <textarea data-testid="answer-input" value={answer} onChange={(e) => setAnswer(e.target.value)} rows={6} placeholder="Type your answer here…"
              className="mt-4 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-primary resize-none" />
            <button data-testid="submit-answer-btn" onClick={submit} disabled={evaluating || !answer.trim()}
              className="mt-3 inline-flex items-center gap-2 bg-primary hover:bg-primary/90 px-5 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50">
              {evaluating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Get AI feedback
            </button>

            {feedback && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 border-t border-white/10 pt-5" data-testid="feedback-panel">
                <div className="flex items-center justify-between">
                  <span className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Score</span>
                  <span className="font-heading text-3xl font-extrabold text-primary">{feedback.score}<span className="text-base text-zinc-500">/100</span></span>
                </div>
                <p className="text-sm text-zinc-300 mt-3">{feedback.ai_feedback}</p>
                {feedback.strengths?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-emerald-400 font-semibold mb-1">Strengths</p>
                    <ul className="text-sm text-zinc-400 list-disc list-inside space-y-0.5">{feedback.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </div>
                )}
                {feedback.improvements?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-accent font-semibold mb-1">To improve</p>
                    <ul className="text-sm text-zinc-400 list-disc list-inside space-y-0.5">{feedback.improvements.map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>
        )}
      </div>

      {history.length > 0 && (
        <div className="mt-10">
          <p className="text-xs tracking-wider uppercase font-semibold text-zinc-500 mb-3 flex items-center gap-2"><RefreshCw className="w-3 h-3" /> Recent practice</p>
          <div className="space-y-2" data-testid="interview-history">
            {history.slice(0, 5).map((h) => (
              <div key={h.interview_id} className="glass rounded-xl p-4 flex items-center justify-between gap-4">
                <p className="text-sm text-zinc-300 truncate">{h.question}</p>
                <span className="text-sm font-semibold text-primary shrink-0 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />{h.score}/100</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
